import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, Check, Copy, Gift, Handshake, Send, X } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Button, cx, EmptyState, Input, Skeleton } from "@/components/ui";
import { api, ApiError, type PartnerCard } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { msg, t } from "@/lib/i18n";

/** Kachelgröße im Raster: klein 1×1, breit 2×1, groß 2×2 */
const SIZE_CLASS: Record<PartnerCard["size"], string> = {
  s: "",
  m: "sm:col-span-2",
  l: "sm:col-span-2 sm:row-span-2",
};

/** Hintergrund ohne Titelbild: Verlauf aus der Akzentfarbe mit feinem Muster */
const tileBackground = (accent: string) => ({
  backgroundImage: `radial-gradient(circle at 85% 15%, rgba(255,255,255,0.18) 0, rgba(255,255,255,0) 45%), radial-gradient(circle at 10% 110%, rgba(0,0,0,0.35) 0, rgba(0,0,0,0) 55%), linear-gradient(135deg, ${accent}, ${accent}cc)`,
});
const initials = (name: string) => name.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

export function PartnerLogo({ p, className }: { p: Pick<PartnerCard, "name" | "logoUrl" | "accent">; className?: string }) {
  return (
    <span className={cx("grid place-items-center rounded-xl bg-white shadow-sm overflow-hidden shrink-0", className)}>
      {p.logoUrl ? <img src={p.logoUrl} alt="" className="size-full object-contain p-1.5" /> : <span className="text-[13px] font-bold" style={{ color: p.accent }}>{initials(p.name)}</span>}
    </span>
  );
}

/** eine Kachel im Raster – Titelbild oder Farbverlauf, Logo, Name, Vorteil */
export function PartnerTile({ p, onOpen }: { p: PartnerCard; onOpen?: () => void }) {
  const big = p.size === "l";
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cx("group relative h-full w-full overflow-hidden rounded-2xl text-left text-white shadow-sm ring-1 ring-black/5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink", SIZE_CLASS[p.size])}
      style={p.coverUrl ? undefined : tileBackground(p.accent)}
    >
      {p.coverUrl && <img src={p.coverUrl} alt="" className="absolute inset-0 size-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />}
      <span className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/0" />
      <span className="relative flex h-full flex-col p-4 sm:p-5">
        <span className="flex items-start justify-between gap-2">
          <PartnerLogo p={p} className={big ? "size-14" : "size-11"} />
          <span className="rounded-full bg-white/15 backdrop-blur px-2.5 py-0.5 text-[11.5px] font-medium">{t(p.category)}</span>
        </span>
        <span className="mt-auto">
          <span className={cx("block font-semibold leading-tight", big ? "text-[26px]" : "text-[17px]")}>{p.name}</span>
          {p.tagline && <span className={cx("mt-1 block text-white/85", big ? "text-[15px] max-w-[460px]" : "text-[12.5px] line-clamp-2")}>{p.tagline}</span>}
          <span className="mt-3 flex flex-wrap items-center gap-2">
            {p.benefit && <span className="inline-flex items-center gap-1.5 rounded-full bg-white text-[12px] font-semibold px-2.5 py-1" style={{ color: p.accent }}><Gift className="size-3.5" />{p.benefit}</span>}
            {p.lastRequestAt && <span className="inline-flex items-center gap-1 rounded-full bg-black/30 px-2 py-0.5 text-[11.5px]"><Check className="size-3" />{t("angefragt")}</span>}
            <span className="ml-auto inline-flex size-8 items-center justify-center rounded-full bg-white/15 backdrop-blur transition-colors group-hover:bg-white group-hover:text-ink"><ArrowUpRight className="size-4" /></span>
          </span>
        </span>
      </span>
    </button>
  );
}

export function PartnersPage() {
  const { tenantId } = useTenant();
  const q = useQuery({ queryKey: ["partners", tenantId], queryFn: () => api.partners.list(tenantId) });
  const [cat, setCat] = useState<string | null>(null);
  const [open, setOpen] = useState<PartnerCard | null>(null);
  const cats = useMemo(() => [...new Set((q.data ?? []).map((p) => p.category))].sort((a, b) => a.localeCompare(b)), [q.data]);
  const list = (q.data ?? []).filter((p) => !cat || p.category === cat);

  return (
    <div className="pb-16">
      <PageHeader title={t("Partner Network")} description={t("Ausgewählte Partner für euren Shop – mit Vorteilen für Logiful-Kunden. Anfragen gehen direkt an den Partner.")} />
      <div className="px-4 md:px-8 space-y-4">
        {cats.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {[null, ...cats].map((c) => (
              <button key={c ?? "all"} onClick={() => setCat(c)} className={cx("h-8 rounded-full px-3.5 text-[13px] font-medium border transition-colors", cat === c ? "bg-ink text-ink-fg border-ink" : "border-border bg-surface hover:bg-surface-2")}>
                {c ? t(c) : t("Alle")}
              </button>
            ))}
          </div>
        )}
        {q.isLoading && <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 auto-rows-[210px] gap-4"><Skeleton className="sm:col-span-2 sm:row-span-2 rounded-2xl" /><Skeleton className="rounded-2xl" /><Skeleton className="rounded-2xl" /><Skeleton className="sm:col-span-2 rounded-2xl" /></div>}
        {q.data && !list.length && <EmptyState icon={<Handshake className="size-5" />} title={t("Noch keine Partner")}>{t("Hier stellt Logiful bald ausgewählte Partner vor.")}</EmptyState>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 auto-rows-[210px] gap-4 [grid-auto-flow:dense]">
          {list.map((p) => <PartnerTile key={p.id} p={p} onOpen={() => setOpen(p)} />)}
        </div>
      </div>
      <PartnerDialog p={open} onClose={() => setOpen(null)} />
    </div>
  );
}

const CATEGORY_LABELS = [msg("Verpackung"), msg("Fotografie"), msg("Finanzierung"), msg("Marketing"), msg("Versicherung"), msg("Software"), msg("Beratung"), msg("Sonstiges")];
void CATEGORY_LABELS;

function PartnerDialog({ p, onClose }: { p: PartnerCard | null; onClose: () => void }) {
  const { tenantId, me } = useTenant();
  const qc = useQueryClient();
  const tenant = me.tenants.find((x) => x.tenantId === tenantId);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [asking, setAsking] = useState(false);
  const reset = () => { setForm({ name: me.user.name ?? "", email: me.user.email, phone: "", company: tenant?.name ?? "", message: "" }); setConsent(false); setAsking(false); };
  const send = useMutation({
    mutationFn: () => api.partners.request(tenantId, p!.id, { message: form.message, contact: { name: form.name, email: form.email, phone: form.phone, company: form.company }, consent: true }),
    onSuccess: () => { toast.success(t("Anfrage gesendet"), { description: t("{name} meldet sich direkt bei euch.", { name: p!.name }) }); void qc.invalidateQueries({ queryKey: ["partners", tenantId] }); onClose(); },
    onError: (e) => toast.error(t("Nicht gesendet"), { description: e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "") : "" }),
  });
  const go = useMutation({
    mutationFn: () => api.partners.click(tenantId, p!.id),
    onSuccess: (r) => window.open(r.url, "_blank", "noopener"),
    onError: () => toast.error(t("Link nicht verfügbar")),
  });
  const copy = () => void navigator.clipboard?.writeText(p!.discountCode).then(() => toast.success(t("Code kopiert")));

  return (
    <Dialog.Root open={!!p} onOpenChange={(o) => { if (!o) onClose(); else reset(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40 animate-fade-in" />
        <Dialog.Content onOpenAutoFocus={() => reset()} className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(620px,calc(100vw-32px))] max-h-[calc(100vh-32px)] overflow-y-auto rounded-2xl border border-border bg-surface shadow-2xl animate-pop-in">
          {p && (
            <>
              <div className="relative h-36 overflow-hidden rounded-t-2xl" style={p.coverUrl ? undefined : tileBackground(p.accent)}>
                {p.coverUrl && <img src={p.coverUrl} alt="" className="absolute inset-0 size-full object-cover" />}
                <span className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <Dialog.Close className="absolute right-3 top-3 size-8 grid place-items-center rounded-full bg-black/30 text-white hover:bg-black/50" aria-label={t("Schließen")}><X className="size-4" /></Dialog.Close>
              </div>
              <div className="px-6 pb-6">
                {/* Profil-Aufbau: Logo ragt ins Titelbild, Name und Kategorie linksbündig darunter */}
                <div>
                  <PartnerLogo p={p} className="relative z-10 -mt-10 size-20 ring-4 ring-surface shadow-[0_4px_14px_rgba(0,0,0,0.12)] border border-border" />
                  <div className="mt-3 min-w-0">
                    <Dialog.Title className="text-[22px] font-semibold leading-tight">{p.name}</Dialog.Title>
                    <p className="mt-0.5 text-[13px] text-fg-muted">{t(p.category)}{p.website && <> · <a href={p.website} target="_blank" rel="noreferrer" className="hover:underline">{new URL(p.website).hostname.replace(/^www\./, "")}</a></>}</p>
                  </div>
                </div>
                {p.tagline && <p className="mt-4 text-[15px] font-medium">{p.tagline}</p>}
                {p.description && <p className="mt-2 text-[13.5px] text-fg-muted whitespace-pre-line">{t(p.description)}</p>}

                {(p.benefit || p.discountCode) && (
                  <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-2/60 p-3">
                    <Gift className="size-5 shrink-0" style={{ color: p.accent }} />
                    <div className="min-w-0 flex-1">
                      <p className="text-[12px] text-fg-muted">{t("Vorteil für Logiful-Kunden")}</p>
                      <p className="text-[14px] font-semibold">{p.benefit || t("Exklusiver Code")}</p>
                    </div>
                    {p.discountCode && <button onClick={copy} className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border-strong bg-surface px-3 h-9 font-mono text-[13px] font-semibold hover:bg-surface-2">{p.discountCode}<Copy className="size-3.5 text-fg-subtle" /></button>}
                  </div>
                )}

                {p.mode === "link" ? (
                  <div className="mt-5 flex justify-end">
                    <Button variant="primary" icon={<ArrowUpRight className="size-4" />} loading={go.isPending} onClick={() => go.mutate()}>{t("Zum Angebot")}</Button>
                  </div>
                ) : !asking ? (
                  <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
                    {p.lastRequestAt && <span className="mr-auto text-[12.5px] text-fg-muted inline-flex items-center gap-1.5"><Check className="size-3.5 text-success" />{t("Zuletzt angefragt am {date}", { date: fmtDateTime(p.lastRequestAt) })}</span>}
                    <Button variant="primary" icon={<Send className="size-4" />} onClick={() => setAsking(true)}>{t("Angebot anfragen")}</Button>
                  </div>
                ) : (
                  <form className="mt-5 space-y-3" onSubmit={(e) => { e.preventDefault(); if (consent) send.mutate(); }}>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("Ansprechpartner")} aria-label={t("Ansprechpartner")} required />
                      <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={t("Firma")} aria-label={t("Firma")} />
                      <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t("E-Mail")} aria-label={t("E-Mail")} required />
                      <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={t("Telefon (optional)")} aria-label={t("Telefon")} />
                    </div>
                    <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={t("Worum geht es? (optional)")} rows={3} className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-ink/40" />
                    <label className="flex items-start gap-2 text-[12.5px] text-fg-muted cursor-pointer">
                      <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-ink" />
                      <span>{t("Ich bin einverstanden, dass Logiful diese Angaben an {name} weitergibt, damit sich der Partner zu meiner Anfrage meldet.", { name: p.name })}</span>
                    </label>
                    <div className="flex justify-end gap-2">
                      <Button type="button" onClick={() => setAsking(false)}>{t("Abbrechen")}</Button>
                      <Button type="submit" variant="primary" icon={<Send className="size-4" />} disabled={!consent} loading={send.isPending}>{t("Anfrage senden")}</Button>
                    </div>
                  </form>
                )}
              </div>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
